import { displayDateUS } from "../../utils/dateUtils";

function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

function ynDisplay(val) {
  if (val === true) return "Yes";
  if (val === false) return "No";
  if (val === "Yes" || val === "No") return val;
  return escapeHtml(val ?? "");
}

/**
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseApplicationFromApiResponse(response, targetUserId) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }
  if (result?.application) {
    return result.application;
  }
  if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    return result.items.find((item) => item.userId === uid) || null;
  }
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null || result.applicantName != null || result.userId != null)
  ) {
    return result;
  }
  return result || null;
}

function educationRowsHtml(educationHistories) {
  const rows = Array.isArray(educationHistories) ? educationHistories : [];
  if (rows.length === 0) {
    return `<tr><td colspan="4" style="border:1px solid #cbd5e1;padding:8px;color:#64748b;">—</td></tr>`;
  }
  return rows
    .map((e) => {
      const school = escapeHtml(e?.schoolNameAddress || "");
      const from = formatDate(e?.attendanceStartDate);
      const to = formatDate(e?.attendanceEndDate);
      const major = escapeHtml(e?.majorSubject || "");
      const deg = escapeHtml(e?.degreeCertificate || "");
      return `<tr>
        <td style="border:1px solid #cbd5e1;padding:6px;vertical-align:top;">${school}</td>
        <td style="border:1px solid #cbd5e1;padding:6px;vertical-align:top;font-size:9pt;">
          From: ${escapeHtml(from)}<br/>To: ${escapeHtml(to)}
        </td>
        <td style="border:1px solid #cbd5e1;padding:6px;vertical-align:top;">${major}</td>
        <td style="border:1px solid #cbd5e1;padding:6px;vertical-align:top;">${deg}</td>
      </tr>`;
    })
    .join("");
}

function referenceRowsHtml(references) {
  const rows = Array.isArray(references) ? references : [];
  if (rows.length === 0) {
    return `<tr><td colspan="5" style="border:1px solid #cbd5e1;padding:8px;color:#64748b;">—</td></tr>`;
  }
  return rows
    .map((r) => {
      return `<tr>
        <td style="border:1px solid #cbd5e1;padding:6px;">${escapeHtml(r?.referenceName || "")}</td>
        <td style="border:1px solid #cbd5e1;padding:6px;">${escapeHtml(r?.referenceAddress || "")}</td>
        <td style="border:1px solid #cbd5e1;padding:6px;">${escapeHtml(r?.referenceTitle || "")}</td>
        <td style="border:1px solid #cbd5e1;padding:6px;">${escapeHtml(r?.referencePhone || "")}</td>
        <td style="border:1px solid #cbd5e1;padding:6px;">${escapeHtml(r?.yearsKnown ?? "")}</td>
      </tr>`;
    })
    .join("");
}

function employmentBlocksHtml(employmentHistories, formatDateShortLine) {
  const rows = Array.isArray(employmentHistories) ? employmentHistories : [];
  if (rows.length === 0) {
    return `<p style="color:#64748b;font-size:10pt;">No employment history provided.</p>`;
  }
  return rows
    .map((h, idx) => {
      const n = idx + 1;
      const may = escapeHtml(h?.mayContactForReference || "");
      return `
      <div style="border:1px solid #cbd5e1;border-radius:8px;padding:12px;margin-bottom:14px;background:#fff;">
        <div style="font-weight:700;font-size:11pt;color:#0f172a;margin-bottom:8px;">Employer ${n}</div>
        <div class="row2"><span class="lbl">Employer</span><span class="val">${escapeHtml(h?.employer || "")}</span></div>
        <div class="row2"><span class="lbl">Address</span><span class="val">${escapeHtml(h?.address || "")}</span></div>
        <div class="row2">
          <span class="lbl">City / State / Zip</span>
          <span class="val">${escapeHtml([h?.city, h?.state, h?.zipCode].filter(Boolean).join(", "))}</span>
        </div>
        <div class="row2"><span class="lbl">Telephone</span><span class="val">${escapeHtml(h?.telephoneNo || "")}</span></div>
        <div class="row2"><span class="lbl">Supervisor / title</span><span class="val">${escapeHtml(h?.immediateSupervisorTitle || "")}</span></div>
        <div class="row2"><span class="lbl">Salary / wages</span><span class="val">${escapeHtml(h?.salaryWages || "")}</span></div>
        <div class="row2"><span class="lbl">Job title / responsibility</span><span class="val">${escapeHtml(h?.jobTitleResponsibility || "")}</span></div>
        <div class="row2"><span class="lbl">Employment</span>
          <span class="val">From ${escapeHtml(formatDateShortLine(h?.employmentFromDate))} &nbsp;to&nbsp; ${escapeHtml(formatDateShortLine(h?.employmentToDate))}</span>
        </div>
        <div class="row2"><span class="lbl">May contact for reference?</span><span class="val">${may || "—"}</span></div>
      </div>`;
    })
    .join("");
}

/**
 * Multi-page Employment Application print HTML (API shape from GET /employment-applications/:id).
 * Styling aligned with referenceBackgroundCheckPrint (paper preview + clean print).
 */
export function buildApplicationFormPrintHtml(app, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");

  const name = escapeHtml(app?.applicantName || "");
  const ssn = escapeHtml(String(app?.ssn || ""));
  const address = escapeHtml(String(app?.address || ""));
  const homePhone = escapeHtml(String(app?.homePhone || ""));
  const cellPhone = escapeHtml(String(app?.cellPhone || ""));
  const fax = escapeHtml(String(app?.faxNumber || ""));
  const email = escapeHtml(String(app?.email || ""));
  const position = escapeHtml(String(app?.positionAppliedFor || ""));
  const pay = escapeHtml(String(app?.desiredPayRate || ""));
  const licNo = escapeHtml(String(app?.licenseCertificateNo || ""));
  const licState = escapeHtml(String(app?.licenseCertificateState || ""));
  const licExp = formatDate(app?.licenseCertificateExpirationDate);
  const dlNo = escapeHtml(String(app?.driversLicenseNo || ""));
  const dlState = escapeHtml(String(app?.driversLicenseState || ""));
  const dlExp = formatDate(app?.driversLicenseExpirationDate);

  const workUs = ynDisplay(app?.usWorkEligibility);
  const fired = ynDisplay(app?.firedOrResigned);
  const convict = ynDisplay(app?.hasConvictionRecord);

  const emergencyName = escapeHtml(String(app?.emergencyContactName || ""));
  const emergencyAddr = escapeHtml(String(app?.emergencyContactAddress || ""));
  const emergencyPhone = escapeHtml(String(app?.emergencyContactPhone || ""));
  const emergencyRel = escapeHtml(String(app?.emergencyContactRelationship || ""));

  const applicantSig = escapeHtml(String(app?.applicantSignature || ""));
  const applicantSigned = formatDate(app?.applicantSignedAt || app?.applicantStatementSignedAt);
  const reviewedBy = escapeHtml(String(app?.reviewedBySignedBy || ""));
  const reviewedSig = escapeHtml(String(app?.reviewedBySignature || ""));
  const reviewedAt = formatDate(app?.reviewedBySignedAt);

  const ack = !!(app?.applicantStatementAccepted || applicantSig);

  const formatShort = (v) => formatDate(v);

  const edu = educationRowsHtml(app?.educationHistories);
  const refRows = referenceRowsHtml(app?.references);
  const empHtml = employmentBlocksHtml(app?.employmentHistories, formatShort);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Employment Application — ${name || "Applicant"}</title>
  <style>
    @page { margin: 0.45in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 10.5pt;
      line-height: 1.4;
      color: #0f172a;
      margin: 0;
      padding: 12px;
    }
    .sheet { max-width: 880px; margin: 0 auto; }
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body { padding: 28px 18px 40px; background: transparent; min-height: 100vh; }
      .sheet {
        background: #fff;
        padding: 28px 32px 36px;
        border: 1px solid #e2e8f0;
        border-radius: 1px;
        box-shadow: 0 1px 2px rgba(15,23,42,0.05), 0 24px 48px -16px rgba(15,23,42,0.2);
      }
    }
    @media print {
      html { background: #fff !important; }
      body { padding: 0 !important; background: #fff !important; }
      .sheet { padding: 0 !important; border: none !important; box-shadow: none !important; background: #fff !important; }
      .page-break { page-break-after: always; }
    }
    .doc-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      padding-bottom: 12px;
      border-bottom: 2px solid #1e293b;
      margin-bottom: 16px;
    }
    .logo { height: 52px; width: auto; max-width: 180px; object-fit: contain; }
    .doc-title-block { text-align: right; flex: 1; }
    .doc-title { font-size: 15pt; font-weight: 700; color: #0f172a; margin: 0 0 4px 0; letter-spacing: 0.02em; }
    .doc-sub { font-size: 10pt; color: #64748b; margin: 0; }
    .section-h {
      font-size: 11pt;
      font-weight: 700;
      color: #1e293b;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      margin: 18px 0 10px 0;
      padding-bottom: 6px;
      border-bottom: 1px solid #e2e8f0;
    }
    .box {
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 12px 14px;
      margin-bottom: 14px;
      background: #fafafa;
    }
    .row2 {
      display: grid;
      grid-template-columns: 160px 1fr;
      gap: 8px 12px;
      margin-bottom: 6px;
      font-size: 10pt;
      align-items: baseline;
    }
    .lbl { font-weight: 600; color: #475569; }
    .val { border-bottom: 1px solid #cbd5e1; min-height: 18px; color: #0f172a; }
    .chk { display: inline-flex; align-items: center; gap: 6px; margin-right: 14px; font-size: 10pt; }
    table.data { width: 100%; border-collapse: collapse; font-size: 9.5pt; margin-top: 6px; }
    table.data th {
      background: #f1f5f9;
      font-weight: 600;
      color: #334155;
      text-align: left;
      padding: 6px;
      border: 1px solid #cbd5e1;
    }
    .statement {
      font-size: 9.5pt;
      line-height: 1.45;
      color: #334155;
      text-align: justify;
      margin-bottom: 10px;
    }
    .warn { font-weight: 700; font-size: 10pt; color: #0f172a; margin: 12px 0 8px 0; text-transform: uppercase; }
    .sig-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-top: 14px;
    }
    .sig-card {
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 10px;
      background: #fff;
    }
    .small-h { font-size: 9pt; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px; }
    .page-footer-note {
      margin-top: 20px;
      padding-top: 10px;
      border-top: 1px solid #e2e8f0;
      font-size: 8.5pt;
      color: #94a3b8;
      text-align: center;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <!-- Page 1 -->
    <div class="page-break">
      <div class="doc-header">
        <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
        <div class="doc-title-block">
          <p class="doc-title">Employment Application</p>
          <p class="doc-sub">${company}</p>
        </div>
      </div>

      <div class="section-h">Applicant information</div>
      <div class="box">
        <div class="row2"><span class="lbl">Full name</span><span class="val">${name || "—"}</span></div>
        <div class="row2"><span class="lbl">SSN</span><span class="val">${ssn || "—"}</span></div>
        <div class="row2"><span class="lbl">Address</span><span class="val">${address || "—"}</span></div>
        <div class="row2"><span class="lbl">Home phone</span><span class="val">${homePhone || "—"}</span></div>
        <div class="row2"><span class="lbl">Cell / other</span><span class="val">${cellPhone || "—"}</span></div>
        <div class="row2"><span class="lbl">Fax</span><span class="val">${fax || "—"}</span></div>
        <div class="row2"><span class="lbl">E-mail</span><span class="val">${email || "—"}</span></div>
        <div class="row2"><span class="lbl">Position applied for</span><span class="val">${position || "—"}</span></div>
        <div class="row2"><span class="lbl">Desired rate of pay</span><span class="val">${pay || "—"}</span></div>
        <div class="row2"><span class="lbl">License / certificate</span><span class="val">${licNo || "—"}</span></div>
        <div class="row2"><span class="lbl">License state / expiration</span><span class="val">${licState || "—"} &nbsp; ${escapeHtml(licExp) || "—"}</span></div>
        <div class="row2"><span class="lbl">Driver's license</span><span class="val">${dlNo || "—"}</span></div>
        <div class="row2"><span class="lbl">DL state / expiration</span><span class="val">${dlState || "—"} &nbsp; ${escapeHtml(dlExp) || "—"}</span></div>
      </div>

      <div class="section-h">Qualifying questions</div>
      <div class="box">
        <p style="font-size:10pt;margin:0 0 8px 0;"><strong>1.</strong> US work authorization (citizen or authorized alien)?</p>
        <div style="margin-bottom:10px;">
          <span class="chk"><input type="checkbox" disabled ${workUs === "Yes" ? "checked" : ""} /> Yes</span>
          <span class="chk"><input type="checkbox" disabled ${workUs === "No" ? "checked" : ""} /> No</span>
        </div>
        <p style="font-size:10pt;margin:0 0 8px 0;"><strong>2.</strong> Ever fired or asked to resign?</p>
        <div style="margin-bottom:10px;">
          <span class="chk"><input type="checkbox" disabled ${fired === "Yes" ? "checked" : ""} /> Yes</span>
          <span class="chk"><input type="checkbox" disabled ${fired === "No" ? "checked" : ""} /> No</span>
        </div>
        <p style="font-size:10pt;margin:0 0 8px 0;"><strong>3.</strong> Conviction / probation / suspended sentence (excluding minor traffic)?</p>
        <div>
          <span class="chk"><input type="checkbox" disabled ${convict === "Yes" ? "checked" : ""} /> Yes</span>
          <span class="chk"><input type="checkbox" disabled ${convict === "No" ? "checked" : ""} /> No</span>
        </div>
      </div>

      <div class="section-h">Educational background</div>
      <table class="data">
        <thead>
          <tr>
            <th>School / address</th>
            <th>Dates attended</th>
            <th>Major / course</th>
            <th>Degree / certificate</th>
          </tr>
        </thead>
        <tbody>${edu}</tbody>
      </table>

      <div class="section-h">Emergency contact</div>
      <div class="box">
        <div class="row2"><span class="lbl">Name</span><span class="val">${emergencyName || "—"}</span></div>
        <div class="row2"><span class="lbl">Address</span><span class="val">${emergencyAddr || "—"}</span></div>
        <div class="row2"><span class="lbl">Phone</span><span class="val">${emergencyPhone || "—"}</span></div>
        <div class="row2"><span class="lbl">Relationship</span><span class="val">${emergencyRel || "—"}</span></div>
      </div>

      <div class="section-h">References</div>
      <table class="data">
        <thead>
          <tr>
            <th>Name</th>
            <th>Address</th>
            <th>Title</th>
            <th>Phone</th>
            <th>Yrs known</th>
          </tr>
        </thead>
        <tbody>${refRows}</tbody>
      </table>
      <div class="page-footer-note">${company} · Page 1</div>
    </div>

    <!-- Page 2+ Employment history -->
    <div class="page-break">
      <div class="doc-header">
        <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
        <div class="doc-title-block">
          <p class="doc-title">Employment history</p>
          <p class="doc-sub">${company} · continued</p>
        </div>
      </div>
      ${empHtml}
      <div class="page-footer-note">${company} · Employment history</div>
    </div>

    <!-- Final page: Applicant statement & signatures -->
    <div>
      <div class="doc-header">
        <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
        <div class="doc-title-block">
          <p class="doc-title">Applicant statement</p>
          <p class="doc-sub">${company}</p>
        </div>
      </div>

      <div class="box">
        <p class="statement">
          I certify that all information provided in this application is true and complete. I understand that false or incomplete information may result in rejection or termination if hired. I authorize the employer to verify information and conduct background checks as permitted by law.
        </p>
        <p class="statement">
          If hired, I agree to comply with policies and procedures. Employment is at-will unless otherwise agreed in writing by an authorized officer.
        </p>
        <div class="warn">Do not sign until you have read the above statement.</div>
        <div style="display:flex;align-items:flex-start;gap:10px;margin-top:8px;font-size:10pt;">
          <input type="checkbox" disabled ${ack ? "checked" : ""} />
          <span>I certify that I have read and accept the foregoing applicant statement.</span>
        </div>
      </div>

      <div class="sig-grid">
        <div class="sig-card">
          <div class="small-h">Employee</div>
          <div class="row2"><span class="lbl">Name</span><span class="val">${name || "—"}</span></div>
          <div class="row2"><span class="lbl">Signature</span><span class="val">${applicantSig || "—"}</span></div>
          <div class="row2"><span class="lbl">Date</span><span class="val">${escapeHtml(applicantSigned) || "—"}</span></div>
        </div>
        <div class="sig-card">
          <div class="small-h">Reviewer / Admin</div>
          <div class="row2"><span class="lbl">Name</span><span class="val">${reviewedBy || "—"}</span></div>
          <div class="row2"><span class="lbl">Signature</span><span class="val">${reviewedSig || "—"}</span></div>
          <div class="row2"><span class="lbl">Date</span><span class="val">${escapeHtml(reviewedAt) || "—"}</span></div>
        </div>
      </div>

      <div class="page-footer-note">${company} · Employment Application</div>
    </div>
  </div>
</body>
</html>`;
}
